import { Component, effect, inject } from '@angular/core';
import { Dialog } from '../dialog';
import {
  BicString,
  CreditInstituteString,
  EEntityType,
  ERelationType,
  IBankData,
  ICreateBankData,
  IbanString,
  IInstitutionData,
} from '@web/dtos';
import { BankDataStore } from '../../../services/api/bank-data/bank-data.store';
import { DialogComponent } from '../dialog/dialog.component';
import { TextInputComponent } from '../../data-input/form-inputs/text-input/text-input.component';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ButtonComponent } from '../../feedback/button/button.component';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { CheckboxComponent } from '../../data-input/form-inputs/checkbox/checkbox.component';
import { TemplateStringPipe } from '../../../pipes/template-string/TemplateStringPipe.pipe';
import { debounceTime, Subscription } from 'rxjs';
import { IbanValidationService } from '../../../services/api/iban-validation/iban-validation.service';
import { PaymentInstituteDataStore } from '../../../services/api/payment-institute-data/payment-institute-data.store';

export interface IBankDataForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  iban: FormControl<IbanString>;
  bic: FormControl<BicString>;
  creditInstitute: FormControl<CreditInstituteString>;
  sepaAgreement: FormControl<boolean>;
}

@Component({
  selector: 'aw-bank-data-dialog',
  imports: [
    DialogComponent,
    TextInputComponent,
    ReactiveFormsModule,
    ButtonComponent,
    FilterTextsPipe,
    LanguagePipe,
    CheckboxComponent,
    TemplateStringPipe,
  ],
  templateUrl: './bank-data-dialog.component.html',
})
export class BankDataDialogComponent extends Dialog<{
  bankData: IBankData;
}> {
  private readonly bankDataStore: BankDataStore = inject(BankDataStore);
  protected readonly instituteDataStore: PaymentInstituteDataStore = inject(
    PaymentInstituteDataStore
  );
  private readonly ibanValidation: IbanValidationService = inject(
    IbanValidationService
  );
  private readonly formBuilder: FormBuilder = inject(FormBuilder);

  protected readonly textStore: WebTextStore = inject(WebTextStore);

  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;

  protected readonly bankDataForm: FormGroup<IBankDataForm> =
    this.formBuilder.group({
      firstName: this.formBuilder.nonNullable.control<string>('', [
        Validators.required,
      ]),
      lastName: this.formBuilder.nonNullable.control<string>('', [
        Validators.required,
      ]),
      iban: this.formBuilder.nonNullable.control<IbanString>(
        '' as IbanString,
        [
          Validators.required,
          Validators.pattern(/[a-zA-Z]{2}[a-zA-Z0-9]{5,34}/),
        ],
        [this.isSepaCountryCode(), this.isNotBlacklisted()]
      ),
      bic: this.formBuilder.nonNullable.control<BicString>('' as BicString, [
        Validators.required,
      ]),
      creditInstitute:
        this.formBuilder.nonNullable.control<CreditInstituteString>(
          '' as CreditInstituteString,
          [Validators.required]
        ),
      sepaAgreement: this.formBuilder.nonNullable.control<boolean>(false, [
        Validators.requiredTrue,
      ]),
    });

  private readonly subscription: Subscription = new Subscription();

  constructor() {
    super();
    this.bankDataForm.controls.firstName.setValue(this.data.bankData.firstName);
    this.bankDataForm.controls.lastName.setValue(this.data.bankData.lastName);
    this.bankDataForm.controls.iban.setValue(this.data.bankData.iban);
    this.bankDataForm.controls.bic.setValue(this.data.bankData.bic);
    this.bankDataForm.controls.creditInstitute.setValue(
      this.data.bankData.creditInstitute
    );
    effect(() => {
      const instituteData: IInstitutionData =
        this.instituteDataStore.instituteData();
      if (instituteData.bic) {
        this.bankDataForm.controls.bic.setValue(instituteData.bic);
        this.bankDataForm.controls.bic.disable();
      }
      if (instituteData.creditInstitute) {
        this.bankDataForm.controls.creditInstitute.setValue(
          instituteData.creditInstitute
        );
        this.bankDataForm.controls.creditInstitute.disable();
      }
    });
    this.subscription.add(
      this.bankDataForm.controls.iban.statusChanges
        .pipe(debounceTime(150))
        .subscribe(status => {
          if (status === 'VALID') {
            this.instituteDataStore.loadInstituteData(
              this.bankDataForm.controls.iban.value
            );
          } else {
            this.instituteDataStore.reload();
            this.bankDataForm.controls.bic.enable();
            this.bankDataForm.controls.creditInstitute.enable();
          }
        })
    );
  }

  protected saveChanges(): void {
    this.bankDataForm.disable();
    const { firstName, lastName, iban, bic, creditInstitute } =
      this.bankDataForm.getRawValue();
    const bankDataToCreate: ICreateBankData = {
      firstName,
      lastName,
      iban,
      bic,
      creditInstitute,
    };
    this.bankDataStore.addBankData(bankDataToCreate).then(() => {
      this.bankDataStore.reload();
      this.dialogRef.close();
    });
  }

  private isSepaCountryCode(): AsyncValidatorFn {
    return async (
      control: AbstractControl
    ): Promise<ValidationErrors | null> => {
      const isSepaCountry: boolean =
        await this.ibanValidation.isSepaCountryCode(control.value.slice(0, 2));
      return isSepaCountry ? null : { notSepaCompliant: true };
    };
  }

  private isNotBlacklisted(): AsyncValidatorFn {
    return async (
      control: AbstractControl
    ): Promise<ValidationErrors | null> => {
      const isNotBlacklisted: boolean =
        await this.ibanValidation.isNotBlacklisted(control.value);
      return isNotBlacklisted ? null : { isBlacklisted: true };
    };
  }
}
